/* eslint-disable react-hooks/exhaustive-deps */
import { ebookBookTemplateActions } from '@orientaction/api-actions';
import { BilingualVideoValue } from '../../../../shared/components/VideoImport/bilingual';
import { ebookBookValues } from '@orientaction/reducers';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { apiServiceNative } from '../../../../../../utils/fetchNative';
import {
  createEmptyBlock,
  EbookContentBlock,
  getQuestionType,
  normalizeEbookContentBlocks,
  QuestionType,
} from '../types';

const WINDOW_SIZE = 120;
const WINDOW_MARGIN = 40;

export const useEditEbook = () => {
  const { id }: { id?: string } = useParams();
  const valuesEbooksTemplate = useSelector(ebookBookValues);
  const dispatch = useDispatch();
  const history = useHistory();

  const [templateBlocks, setTemplateBlocks] = useState<any[]>([]);
  const [allBlocks, setAllBlock] = useState<EbookContentBlock[]>([]);
  const [indexSelected, setIndexSelected] = useState(0);
  const [blockSelected, setBlockSelected] = useState<EbookContentBlock>({});
  const [valueInput, setValueInput] = useState<number | ''>('');
  const [pulseMove, setPulseMove] = useState(-1);
  const [showMenu, setShowMenu] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openExit, setOpenExit] = useState(false);
  const [openPublish, setOpenPublish] = useState(false);
  const [loading, setLoading] = useState(false);
  const [textLoading, setTextLoading] = useState('');
  const [renderStart, setRenderStart] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(null);
  const [loadingTop, setLoadingTop] = useState(false);
  const [loadingBottom, setLoadingBottom] = useState(false);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [openEditImg, setOpenEditImg] = useState(false);
  const [openDialogVideo, setOpenDialogVideo] = useState(false);
  const [url, setUrl] = useState('');
  const [imageDimensions, setImageDimensions] = useState<{ height?: number; width?: number }>({});
  const [indexEditImg, setIndexEditImg] = useState<number | null>(null);
  const [dataImg, setDataImg] = useState({ imgLink: '', checkedLink: false });
  const [stateEdition, setStateEdition] = useState<any>();
  const [position, setPosition] = useState<string>();

  const imgRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollLoadingRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);

  const syncBlocks = useCallback(
    (blocks: EbookContentBlock[]) => {
      setAllBlock(blocks);
      dispatch(ebookBookTemplateActions.updateField('content', blocks));
    },
    [dispatch]
  );

  const scrollToBottom = useCallback(
    (blocksArray?: EbookContentBlock[]) => {
      const blocks = blocksArray || allBlocks;
      const total = blocks?.length || 0;
      if (total === 0) return;

      const lastBlockIndex = total - 1;
      const windowStart = Math.max(0, lastBlockIndex - WINDOW_SIZE + 1);

      isProgrammaticScrollRef.current = true;
      setIsNavigating(true);
      setIndexSelected(lastBlockIndex);
      setValueInput(lastBlockIndex + 1);
      if (blocks[lastBlockIndex]) setBlockSelected(blocks[lastBlockIndex]);
      setRenderStart(windowStart);
      setPendingScrollIndex(lastBlockIndex);
    },
    [allBlocks]
  );

  const scrollToSection = useCallback(
    (index: number) => {
      isProgrammaticScrollRef.current = true;
      setIsNavigating(true);
      setIndexSelected(index);
      setValueInput(index + 1);
      if (allBlocks[index]) setBlockSelected(allBlocks[index]);

      const total = allBlocks?.length || 0;
      const windowStart = Math.max(0, Math.min(index - WINDOW_MARGIN, total - WINDOW_SIZE));
      setRenderStart(windowStart);

      setTimeout(() => {
        setPendingScrollIndex(index);
      }, 100);
    },
    [allBlocks]
  );

  useEffect(() => {
    sessionStorage.setItem('book', 'true');

    const fetchTemplates = async () => {
      try {
        const cached = sessionStorage.getItem('bloc-templates');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            setTemplateBlocks(parsed);
            return;
          }
        }

        const dataTemplateApi = await apiServiceNative.get(
          '/api/bloc-templates?populate=*&sort=id:asc'
        );
        const templates = dataTemplateApi?.data || [];
        setTemplateBlocks(templates);
        sessionStorage.setItem('bloc-templates', JSON.stringify(templates));
      } catch (error) {
        console.error('Error fetching bloc templates:', error);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedId = sessionStorage.getItem('id') || id;
        if (storedId && !sessionStorage.getItem('fromEdit')) {
          const response = await apiServiceNative.get(`/api/ebook-details/${storedId}`);
          dispatch(ebookBookTemplateActions.setAllValues(response));
          setAllBlock(normalizeEbookContentBlocks(response?.content));
          sessionStorage.setItem('id', storedId);
        } else {
          setAllBlock(normalizeEbookContentBlocks(valuesEbooksTemplate?.content));
        }
        sessionStorage.removeItem('fromEdit');
      } catch (error) {
        console.error('Error fetching ebook data:', error);
      }
    };

    fetchData();
  }, [dispatch, id]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastScrollTop = el.scrollTop;

    const updateSidebarHighlight = () => {
      const containerTop = el.getBoundingClientRect().top;
      const containerHeight = el.clientHeight;
      const viewportCenter = containerTop + containerHeight / 2;

      let closestTitleIdx = currentTitleIndex;
      let closestDistance = Infinity;
      const startIdx = Math.max(0, renderStart);
      const endIdx = Math.min(renderStart + WINDOW_SIZE, allBlocks.length);

      for (let blockIndex = startIdx; blockIndex < endIdx; blockIndex += 1) {
        const block = allBlocks[blockIndex];
        if (block?.bloc_template === 2) {
          const titleElement = document.getElementById(`title-${blockIndex}`);
          if (titleElement) {
            const rect = titleElement.getBoundingClientRect();
            if (rect.height > 0 && rect.width > 0) {
              const titleCenter = rect.top + rect.height / 2;
              const distance = Math.abs(titleCenter - viewportCenter);
              if (distance < closestDistance && rect.top < viewportCenter) {
                closestDistance = distance;
                closestTitleIdx = blockIndex;
              }
            }
          }
        }
      }

      if (closestTitleIdx !== currentTitleIndex) {
        setCurrentTitleIndex(closestTitleIdx);
      }
    };

    const handleScrollUpdate = () => {
      if (scrollLoadingRef.current) return;

      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;
      const nearTop = el.scrollTop <= 200;
      const total = allBlocks?.length || 0;

      updateSidebarHighlight();

      if (nearBottom && renderStart + WINDOW_SIZE < total) {
        scrollLoadingRef.current = true;
        setLoadingBottom(true);
        const next = Math.min(renderStart + 40, Math.max(0, total - WINDOW_SIZE));
        if (next !== renderStart) {
          setRenderStart(next);
          setTimeout(() => {
            scrollLoadingRef.current = false;
            setLoadingBottom(false);
          }, 300);
        } else {
          scrollLoadingRef.current = false;
          setLoadingBottom(false);
        }
      } else if (nearTop && renderStart > 0) {
        scrollLoadingRef.current = true;
        setLoadingTop(true);
        const next = Math.max(0, renderStart - 40);
        if (next !== renderStart) {
          setRenderStart(next);
          setTimeout(() => {
            scrollLoadingRef.current = false;
            setLoadingTop(false);
          }, 300);
        } else {
          scrollLoadingRef.current = false;
          setLoadingTop(false);
        }
      }
    };

    const onScroll = () => {
      const currentScrollTop = el.scrollTop;
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);
      lastScrollTop = currentScrollTop;

      if (scrollDelta < 5 && isProgrammaticScrollRef.current) return;

      if (isProgrammaticScrollRef.current || isNavigating) {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (!isProgrammaticScrollRef.current && !isNavigating) {
            handleScrollUpdate();
          }
        }, 150);
        return;
      }

      handleScrollUpdate();
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [renderStart, allBlocks, currentTitleIndex, isNavigating]);

  useEffect(() => {
    if (pendingScrollIndex === null) return undefined;

    isProgrammaticScrollRef.current = true;
    let attempts = 0;
    const maxAttempts = 20;

    const scrollToElement = () => {
      const titleElement = document.getElementById(`title-${pendingScrollIndex}`);
      const blockElement = document.querySelector(`[data-block-index="${pendingScrollIndex}"]`);
      const element = titleElement || blockElement;

      if (element) {
        const isVisible = element.getBoundingClientRect().height > 0;
        if (isVisible) {
          element.scrollIntoView({ behavior: 'auto', block: titleElement ? 'start' : 'end' });
          setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 200);
          setIsNavigating(false);
          setPendingScrollIndex(null);
          return true;
        }
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        requestAnimationFrame(scrollToElement);
      } else {
        isProgrammaticScrollRef.current = false;
        setIsNavigating(false);
        setPendingScrollIndex(null);
      }
      return false;
    };

    requestAnimationFrame(scrollToElement);
    return undefined;
  }, [pendingScrollIndex]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const message = 'Êtes-vous sûr de vouloir quitter sans sauvegarder vos modifications ?';
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleChangeInput = (value: unknown, index: number, field: string) => {
    setAllBlock(prevBlocks => {
      const newItems = [...prevBlocks];
      newItems[index] = { ...newItems[index], [field]: value };
      dispatch(ebookBookTemplateActions.updateField('content', newItems));
      return newItems;
    });
  };

  const handleChangeOption = (blockIndex: number, responseIndex: number, value: string) => {
    const newBlocks = [...allBlocks];
    if (newBlocks[blockIndex].qcm?.[0]?.response) {
      newBlocks[blockIndex].qcm![0].response![responseIndex].responseText = value;
    }
    syncBlocks(newBlocks);
  };

  const handleChangeQuestionType = (blockIndex: number, type: QuestionType) => {
    const newBlocks = [...allBlocks];
    const qcm = newBlocks[blockIndex].qcm ? [...newBlocks[blockIndex].qcm!] : [];
    if (qcm.length > 0) {
      qcm[0] = { ...qcm[0], type };
    } else {
      qcm.push({ idx: 1, question: '', type, response: [] });
    }
    newBlocks[blockIndex].qcm = qcm;
    syncBlocks(newBlocks);
  };

  const handleAddOption = (index: number, isOther = false) => {
    const newBlocks = [...allBlocks];
    const newQcm = newBlocks[index].qcm ? [...newBlocks[index].qcm!] : [];
    const newResponse = newQcm[0]?.response ? [...newQcm[0].response!] : [];
    newResponse.push({
      idx: newResponse.length + 1,
      responseText: '',
      score: null,
      isOther,
    });
    if (newQcm.length > 0) {
      newQcm[0] = { ...newQcm[0], response: newResponse };
    } else {
      newQcm.push({ idx: 1, question: '', type: 'radio', response: newResponse });
    }
    newBlocks[index].qcm = newQcm;
    syncBlocks(newBlocks);
  };

  const handleDeleteOption = (blockIndex: number, optionIndex: number) => {
    const newBlocks = [...allBlocks];
    if (newBlocks[blockIndex].qcm?.[0]?.response) {
      newBlocks[blockIndex].qcm![0].response = newBlocks[blockIndex].qcm![0].response!.filter(
        (_, i) => i !== optionIndex
      );
    }
    syncBlocks(newBlocks);
  };

  const handleChangeNumLines = (numLines: number, index: number) => {
    const newBlocks = [...allBlocks];
    newBlocks[index].numLines = numLines || 1;
    setAllBlock(newBlocks);
  };

  const moveElement = (fromIndex: number, toIndex: number) => {
    const updatedItems = [...allBlocks];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    syncBlocks(updatedItems);
    setIndexSelected(toIndex);
    setBlockSelected(updatedItems[toIndex]);
    setPulseMove(prev => prev + 1);
  };

  const handleInputChange = (rawValue: string) => {
    const value = rawValue.trim();
    if (value === '') {
      setValueInput('');
      return;
    }
    const numericValue = parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      setValueInput(numericValue);
    }
  };

  const handleMoveToPosition = () => {
    if (allBlocks?.length > 0 && valueInput !== '' && !Number.isNaN(valueInput)) {
      const maxPages = allBlocks.length;
      const clampedValue = valueInput >= maxPages ? maxPages : valueInput < 1 ? 1 : valueInput;
      const targetIndex = clampedValue - 1;
      moveElement(indexSelected, targetIndex);
      scrollToSection(targetIndex);
      setValueInput(clampedValue);
    }
  };

  const handleAddUpdate = async (isPublish?: boolean) => {
    const data = {
      ...valuesEbooksTemplate,
      content: allBlocks,
      publishedAt: isPublish ? new Date() : null,
    };

    localStorage.removeItem('idFILESTemps');
    setTextLoading(isPublish ? 'Publication en cours...' : 'Sauvegarde en cours...');
    setLoading(true);
    const ebookId = sessionStorage.getItem('id');

    try {
      if (ebookId) {
        const response = await apiServiceNative.put(`/api/update-serious-ebook/${ebookId}`, {
          data,
        });
        dispatch(ebookBookTemplateActions.setAllValues(response));
        setAllBlock(normalizeEbookContentBlocks(response?.content));
      } else {
        const response = await apiServiceNative.post('/api/create-ebook', { data });
        dispatch(ebookBookTemplateActions.setAllValues(response));
        setAllBlock(normalizeEbookContentBlocks(response?.content));
        sessionStorage.setItem('id', response?.id);
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteBlock = () => {
    const updatedItems = [...allBlocks];
    updatedItems.splice(indexSelected, 1);
    syncBlocks(updatedItems);
    setPulseMove(prev => prev + 1);
    setOpenModal(false);
  };

  const selectType = (blocTemplate: number) => {
    if (blocTemplate !== 5 && blocTemplate !== 7) {
      const newBlocks = [...allBlocks, createEmptyBlock(blocTemplate)];
      const indexLast = newBlocks.length - 1;
      setAllBlock(newBlocks);
      setShowMenu(false);
      setIndexSelected(indexLast);
      scrollToBottom(newBlocks);
    } else if (blocTemplate === 7) {
      setOpenDialogVideo(true);
    } else {
      imgRef.current?.click();
    }
  };

  const handleChangeImg = (files: FileList | null) => {
    if (!files?.[0]) return;
    const uri = URL.createObjectURL(files[0]);
    setUrl(uri);
    setOpenEditImg(true);
    const img = new Image();
    img.src = uri;
    img.onload = () => {
      setImageDimensions({ height: img.height, width: img.width });
    };
    setShowMenu(false);
  };

  const handleChangeInputImg = (
    uri: string,
    imageId: unknown,
    link: string,
    checkedLink: boolean
  ) => {
    if (indexEditImg === null) {
      if (uri) {
        const newType: EbookContentBlock = {
          ...createEmptyBlock(5),
          img: { id: imageId as number, url: uri },
          imgLink: link,
          checkedLink,
        };
        const newBlocks = [...allBlocks, newType];
        setIndexSelected(newBlocks.length - 1);
        setAllBlock(newBlocks);
        scrollToBottom(newBlocks);
      }
    } else {
      setAllBlock(prevBlocks => {
        const newItems = [...prevBlocks];
        newItems[indexEditImg] = {
          ...newItems[indexEditImg],
          img: { ...newItems[indexEditImg].img, url: uri },
          imgLink: link,
          checkedLink,
        };
        dispatch(ebookBookTemplateActions.updateField('content', newItems));
        return newItems;
      });
      setIndexEditImg(null);
    }
  };

  const handleVideoImportSubmit = (value: BilingualVideoValue) => {
    const { video, videoEn, ...options } = value;
    const newType = { ...createEmptyBlock(7), ...options, videoBook: video || '', videoBookEn: videoEn || '' };
    const newBlocks = [...allBlocks, newType];
    setAllBlock(newBlocks);
    setIndexSelected(newBlocks.length - 1);
    setShowMenu(false);
    scrollToBottom(newBlocks);
    setOpenDialogVideo(false);
  };

  const editStyleEditor = (styleCommand: unknown, arg: unknown) => {
    if (allBlocks[indexSelected]?.bloc_template === 1) {
      setAllBlock(prevData => {
        const dataPrev = [...prevData];
        dataPrev[indexSelected].styleEditor = { styleCommand, arg };
        dispatch(ebookBookTemplateActions.updateField('content', dataPrev));
        return dataPrev;
      });
    }
  };

  const editPosition = (alignement: string) => {
    if (allBlocks[indexSelected]?.bloc_template === 1) {
      handleChangeInput(alignement, indexSelected, 'alignement');
    }
  };

  const addLink = (showLink: boolean) => {
    if (allBlocks[indexSelected]?.bloc_template === 1) {
      setAllBlock(prevData => {
        const dataPrev = [...prevData];
        dataPrev[indexSelected].showLinkPopup = showLink;
        return dataPrev;
      });
    }
  };

  const selectBlock = (absoluteIndex: number) => {
    setIndexSelected(absoluteIndex);
    setBlockSelected(allBlocks[absoluteIndex]);
    setValueInput(absoluteIndex + 1);
  };

  const handleConfirmExit = () => {
    ebookBookTemplateActions.resetValuesEbooks();
    sessionStorage.setItem('fromEbook', 'true');
    history.push('/admin/game/list');
  };

  const handleConfirmPublish = () => {
    handleAddUpdate(true);
    setOpenPublish(false);
  };

  const coverUrl =
    valuesEbooksTemplate?.img?.url ||
    'https://storage.googleapis.com/orient-action.appspot.com/xx_85ae6992d2.png?updated_at=2024-05-27T18:25:35.428Z';

  return {
    WINDOW_SIZE,
    addLink,
    allBlocks,
    blockSelected,
    containerRef,
    coverUrl,
    currentTitleIndex,
    dataImg,
    deleteBlock,
    editPosition,
    editStyleEditor,
    getQuestionType,
    handleAddOption,
    handleAddUpdate,
    handleChangeImg,
    handleChangeInput,
    handleChangeInputImg,
    handleChangeNumLines,
    handleChangeOption,
    handleChangeQuestionType,
    handleConfirmExit,
    handleConfirmPublish,
    handleDeleteOption,
    handleInputChange,
    handleMoveToPosition,
    handleVideoImportSubmit,
    history,
    imageDimensions,
    imgRef,
    indexEditImg,
    indexSelected,
    isNavigating,
    loading,
    loadingBottom,
    loadingTop,
    moveElement,
    openDialogVideo,
    openEditImg,
    openExit,
    openModal,
    openPublish,
    pendingScrollIndex,
    position,
    pulseMove,
    renderStart,
    scrollToSection,
    selectBlock,
    selectType,
    setBlockSelected,
    setCurrentTitleIndex,
    setDataImg,
    setIndexEditImg,
    setIndexSelected,
    setOpenDialogVideo,
    setOpenEditImg,
    setOpenExit,
    setOpenModal,
    setOpenPublish,
    setPosition,
    setShowMenu,
    setStateEdition,
    setUrl,
    setValueInput,
    showMenu,
    stateEdition,
    syncBlocks,
    templateBlocks,
    textLoading,
    url,
    valueInput,
    valuesEbooksTemplate,
  };
};

export type UseEditEbookReturn = ReturnType<typeof useEditEbook>;
